import {
  useCompanyTicketMonthQuery,
  getCompanyIdsWithTicketsInRange,
  paginateNodes,
  type TicketFilterParams,
} from "./companies-ticket-filter-utils";

// ---------------------------------------------------------------------------
// Date range helpers
// ---------------------------------------------------------------------------

function getThisMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
  };
}

function getLastMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
  };
}

// ---------------------------------------------------------------------------
// Shared processing helper
// ---------------------------------------------------------------------------

/**
 * Filters the raw Apollo result to companies that either have or don't have
 * tickets within the given date range, then paginates.
 */
function processCompanyTicketFilter(
  result: ReturnType<typeof useCompanyTicketMonthQuery>,
  start: Date,
  end: Date,
  mode: "with" | "without",
  params?: TicketFilterParams
) {
  const processedData = result.data
    ? (() => {
        const allCompanies =
          result.data.companiesCollection?.edges
            ?.map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => node != null) || [];

        const companyIdsWithTickets = getCompanyIdsWithTicketsInRange(result.data, start, end);

        const filtered =
          mode === "without"
            ? allCompanies.filter((c) => !companyIdsWithTickets.has(c.id))
            : allCompanies.filter((c) => companyIdsWithTickets.has(c.id));

        return paginateNodes(filtered, params);
      })()
    : undefined;

  return {
    ...result,
    data: processedData,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/**
 * Get companies that have NO tickets created this month.
 * Uses GraphQL query and filters client-side since GraphQL doesn't support NOT EXISTS subqueries.
 */
export function useCompaniesWithNoTicketsThisMonth(params?: TicketFilterParams) {
  const result = useCompanyTicketMonthQuery(params);
  const { start, end } = getThisMonthRange();
  return processCompanyTicketFilter(result, start, end, "without", params);
}

/**
 * Get companies that HAVE tickets created this month.
 * Uses GraphQL query and filters client-side since GraphQL doesn't support NOT EXISTS subqueries.
 */
export function useCompaniesWithTicketsThisMonth(params?: TicketFilterParams) {
  const result = useCompanyTicketMonthQuery(params);
  const { start, end } = getThisMonthRange();
  return processCompanyTicketFilter(result, start, end, "with", params);
}

/**
 * Get companies that have NO tickets created last month.
 * Uses GraphQL query and filters client-side since GraphQL doesn't support NOT EXISTS subqueries.
 */
export function useCompaniesWithNoTicketsLastMonth(params?: TicketFilterParams) {
  const result = useCompanyTicketMonthQuery(params);
  const { start, end } = getLastMonthRange();
  return processCompanyTicketFilter(result, start, end, "without", params);
}

/**
 * Get companies that HAVE tickets created last month.
 * Uses GraphQL query and filters client-side since GraphQL doesn't support NOT EXISTS subqueries.
 */
export function useCompaniesWithTicketsLastMonth(params?: TicketFilterParams) {
  const result = useCompanyTicketMonthQuery(params);
  const { start, end } = getLastMonthRange();
  return processCompanyTicketFilter(result, start, end, "with", params);
}
